import { usePreview } from "@core/hooks/usePreview";
import { getModelHash, ModelData } from "@/data/data";
import { ARButton } from "@components/ARButton";
import { PreviewInteractionHint } from "@components/PreviewInteractionHint";
import type { ComponentChildren } from "preact";
import OliveRidleyImage1 from "@assets/turtle-images/olive-ridley-1.webp";
import OliveRidleyImage2 from "@assets/turtle-images/olive-ridley-2.webp";
import OliveRidleyImage3 from "@assets/turtle-images/olive-ridley-3.webp";
import OliveRidleyImage4 from "@assets/turtle-images/olive-ridley-4.webp";

interface OliveRidleySectionProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  onTrackLaunchClick?: (
    model: ModelData,
    options?: { transportType?: "beacon" }
  ) => void;
  launchUrl?: string;
  isChecking: boolean;
  isCompatible: boolean;
}

export function OliveRidleySection({
  model,
  onLaunchAR,
  onTrackLaunchClick,
  launchUrl,
  isChecking,
  isCompatible,
}: OliveRidleySectionProps) {
  const { canvasRef, isFullscreen } = usePreview(model.id);

  const oliveRidleyCopy = {
    row1: {
      left: {
        title: "Small, determined sailors",
        image: OliveRidleyImage1,
        imageAlt: "Olive ridley turtle in ocean",
        imageCaption: "Photo by © Roderick B. Mast-SWOT",
        paragraphs: [
          "The olive ridley is the most abundant sea turtle in the world, roaming the Indian, Pacific and Atlantic oceans. It is Australia’s smallest sea turtle.",
          "In Australian waters they are endangered, and you are more likely see them north of the Fraser Coast where the warmer water suits them best.",
          "Olive ridleys travel thousands of kilometres to nest on the same beach they were born on. In some parts of the world, mothers’ mass and nest together so that more hatchlings make it safely to sea.",
        ],
      },
      right: {
        title: "I’ll eat almost anything",
        image: OliveRidleyImage2,
        imageAlt: "Olive ridley feeding behaviour",
        imageCaption: "Photo by © Roderick B. Mast-SWOT",
        useImageCaptionSpacer: true,
        paragraphs: [
          "Olive ridleys like a varied diet of jellyfish, small crabs, sea squirts, shrimp, fish, and occasionally algae and seaweed.",
          "As they will eat just about anything, they are likely to also snap up rubbish. You can help:",
        ],
        bullets: [
          "reduce single-use plastics",
          "recycle properly",
          "join beach clean-ups",
          "avoid products that use plastic microbeads",
          "divert or reduce chemical run-off when you clean your car or garden.",
        ],
      },
    },
    row2: {
      left: {
        title: "The north is heating up",
        image: OliveRidleyImage3,
        imageAlt: "Olive ridley hatchlings impacted by heat",
        imageCaption: "UniSC research students in action",
        useImageCaptionSpacer: true,
        paragraphs: [
          "As our planet warms, so do our oceans and beaches – and turtle nests are feeling the heat.",
          "Warmer sand slows hatchlings trying to make it to the sea, reduces their fitness, and causes more females than males to be born. Hot sand can even cook turtle eggs before they hatch.",
          "Play your part in reducing and mitigating climate change. Every little bit helps.",
        ],
      },
      right: {
        title: "Taking action across the globe",
        image: OliveRidleyImage4,
        imageAlt: "Olive ridley conservation action",
        imageCaption: "UniSC research students in action",
        paragraphs: [
          <>
            International experts are working hard to better understand turtle
            populations, for example the international{" "}
            <a
              href="https://oliveridleyproject.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline"
            >
              Olive Ridley Project
            </a>
            .
          </>,
          "Research fills the gaps in our knowledge – about populations, habitats, migration patterns and threats. It informs our care, rehabilitation and conservation efforts.",
          "We need people to be conscious of their environment and motivated to protect it. Local communities can bring species back from the brink of extinction.",
        ],
      },
    },
  };

  const handleLaunchAR = (selectedModel: ModelData) => {
    if (selectedModel.arType === "webxr" && isCompatible) {
      console.log("[View AR / App Clip] launchUrl:", launchUrl);
      onLaunchAR(selectedModel);
    }
  };

  const renderModelViewer = () => (
    <div className="w-full relative h-[300px] sm:h-[400px] md:h-[549px]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-[60]"
        tabIndex={-1}
        style={{ width: "100%", height: "100%", outline: "none" }}
      />
    </div>
  );

  const renderFullscreenUI = () => {
    if (!isFullscreen) return null;
    return (
      <div className="fixed inset-0 z-[1000] pointer-events-none animate-fade-in">
        <div className="absolute top-4 left-4 text-white text-lg font-bold drop-shadow-lg">
          Olive ridley turtle
        </div>
        <PreviewInteractionHint />
      </div>
    );
  };

  const renderSectionBody = (section: {
    title: string;
    image: string;
    imageAlt: string;
    imageCaption?: string;
    useImageCaptionSpacer?: boolean;
    paragraphs: ComponentChildren[];
    bullets?: string[];
  }) => (
    <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
      <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
        <div className="w-full h-[200px] sm:h-[240px] md:h-[265px] overflow-hidden bg-gray-300">
          <img
            src={section.image}
            alt={section.imageAlt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        {section.imageCaption ? (
          <p
            aria-hidden={section.useImageCaptionSpacer || undefined}
            className={`${section.useImageCaptionSpacer ? "hidden md:block md:invisible " : ""}text-black text-[12px] font-light leading-[1.2em] italic`}
          >
            {section.imageCaption}
          </p>
        ) : null}
      </div>
      <h3
        className="text-black text-[20px] sm:text-[24px] 2xl:text-[28px]"
        style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          lineHeight: "1.2em",
        }}
      >
        {section.title}
      </h3>
      <div className="flex flex-col gap-2 sm:gap-3">
        {section.paragraphs.map((paragraph, i) => (
          <p
            key={i}
            className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 300,
              lineHeight: "1.2em",
            }}
          >
            {paragraph}
          </p>
        ))}
        {section.bullets && section.bullets.length > 0 ? (
          <ul
            className="list-disc pl-4 md:pl-6 marker:text-[0.7em] text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 300,
              lineHeight: "1.2em",
            }}
          >
            {section.bullets.map((bullet, i) => (
              <li key={i} className={i < section.bullets!.length - 1 ? "mb-2" : ""}>
                {bullet}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );

  return (
    <section
      id={getModelHash(model)}
      className="section-lazy relative overflow-hidden py-12 px-6 sm:py-16 sm:px-10 md:py-20 md:px-20 2xl:py-[120px] 2xl:px-[155px]"
      style={{ backgroundColor: "#F1F1F1" }}
    >
      {renderFullscreenUI()}

      <div className="flex flex-col w-full max-w-[1130px] mx-auto gap-8 sm:gap-10 md:gap-[60px]">
        <div className="flex flex-col gap-1 sm:gap-2 md:gap-2 2xl:gap-3">
          <h2
            className="text-black text-left font-[600] text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px]"
            style={{
              lineHeight: "1em",
              letterSpacing: "-0.72px",
            }}
          >
            Olive ridley turtle
          </h2>
          <h3 className="leading-[1rem] font-light text-[18px] sm:text-[20px] md:text-[24px] 2xl:text-[30px] italic">
            {model.scientificName}
          </h3>
        </div>

        <div className="flex flex-col gap-6 sm:gap-[30px]">
          <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
            {renderModelViewer()}
            <span className="text-[12px] leading-[1.2em] invisible">
              &nbsp;
            </span>
          </div>
          <div className="w-full flex justify-center">
            <ARButton
              model={model}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={onTrackLaunchClick}
              isChecking={isChecking}
              isCompatible={isCompatible}
            />
          </div>
        </div>

        <div className="flex flex-col gap-10 sm:gap-[50px]">
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1">{renderSectionBody(oliveRidleyCopy.row1.left)}</div>
            <div className="flex-1">{renderSectionBody(oliveRidleyCopy.row1.right)}</div>
          </div>
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1">{renderSectionBody(oliveRidleyCopy.row2.left)}</div>
            <div className="flex-1">{renderSectionBody(oliveRidleyCopy.row2.right)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
